import { PadBox } from 'component/basics';
import { ReactionButton } from 'component/button';
import { StyleSheet, View } from 'react-native';
import { useCollection, useDatastore, usePersonaKey } from 'util/datastore';
import { logEventAsync } from 'util/eventlog';
import { useConfig } from 'util/features';
import { reactionTable } from 'util/stdconfig';
import { labelToKey } from 'util/stdtypes';

import type { ConversationHelperConfig, ThoughtProvokingPrompt } from './conversationhelpertypes';
import { thoughtProvokingPromptReactionTable, thoughtProvokingPromptTable } from './conversationhelpertypes';

export type ReactionWithType = { reactionType: string };
export type AutoModeratorReactionTypesWidgetProps = {
    thoughtProvokingPrompt: ThoughtProvokingPrompt;
};

export const AutoModeratorReactionTypesFeature = {
    name: 'AutoModerator Reaction Types',
    key: 'automoderatorreactiontypes',
    providesConfig: {
        commentBelowWidgets: [AutoModeratorReactionTypesWidget],
    },
    receivesConfigDefaults: {},
};

export function countReactionsPerType(reactions: ReactionWithType[]): Record<string, number> {
    const counts: Record<string, number> = {};
    reactions.forEach((reaction) => {
        const { reactionType } = reaction;
        counts[reactionType] = counts[reactionType] || 0;
        counts[reactionType]++;
    });
    return counts;
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function AutoModeratorReactionTypesWidget({ thoughtProvokingPrompt }: AutoModeratorReactionTypesWidgetProps): JSX.Element {
    const s = ReactionTypesWidgetsStyle;
    const { conversationHelperReactionTypes } = useConfig<ConversationHelperConfig>();
    const personaKey = usePersonaKey() as string | null;
    const datastore = useDatastore();
    const thoughtProvokingPrompts = useCollection(thoughtProvokingPromptTable, { sortBy: 'time', reverse: true });
    const isNewest = thoughtProvokingPrompts[0]?.key === thoughtProvokingPrompt.key;
    const reactions = useCollection(thoughtProvokingPromptReactionTable, { filter: { reactTo: thoughtProvokingPrompt.key } });
    const myReactions = useCollection(thoughtProvokingPromptReactionTable, {
        filter: { reactTo: thoughtProvokingPrompt.key, from: personaKey as string },
    });

    const reactionCounts = countReactionsPerType(reactions);
    const myReactionCounts = countReactionsPerType(myReactions);

    function onReact({ reaction }) {
        if (myReactionCounts[reaction.label]) {
            datastore.deleteObject(
                thoughtProvokingPromptReactionTable,
                myReactions.find((r) => r.reactionType == reaction.label)?.key as string,
            );

            logEventAsync(datastore, 'conversationhelper-reaction-undo', {
                reaction: reaction.label,
                reminder: thoughtProvokingPrompt.reminder,
                suggestedPerspective: thoughtProvokingPrompt.suggestedPerspective,
            });
        } else {
            if (reaction.single) {
                const existing = datastore.getCollection(thoughtProvokingPromptReactionTable, {
                    filter: { from: personaKey as string, reactionType: reaction.label },
                });
                existing.forEach((r) => datastore.deleteObject(reactionTable, r.key));
            }

            datastore.addObject(thoughtProvokingPromptReactionTable, {
                reactTo: thoughtProvokingPrompt.key,
                from: personaKey as string,
                reactionType: reaction.label,
            });

            logEventAsync(datastore, 'conversationhelper-reaction', {
                reaction: reaction.label,
                reminder: thoughtProvokingPrompt.reminder,
                suggestedPerspective: thoughtProvokingPrompt.suggestedPerspective,
            });
        }
    }

    const shownReactionTypes = conversationHelperReactionTypes.filter(
        (reaction) => isNewest || reactionCounts[labelToKey(reaction.label)] > 0,
    );

    return (
        <View style={s.outer}>
            {shownReactionTypes.map((reaction, i) => (
                <PadBox key={i} right={!isNewest ? 24 : 16} top={16}>
                    <ReactionButton
                        count={reactionCounts[labelToKey(reaction.label)] || 0}
                        emoji={reaction.emoji}
                        label={reaction.label}
                        onPress={datastore.needsLogin(() => onReact({ reaction }), 'react')}
                        selected={myReactionCounts[labelToKey(reaction.label)] > 0}
                        viewOnly={!isNewest}
                    />
                </PadBox>
            ))}
        </View>
    );
}
const ReactionTypesWidgetsStyle = StyleSheet.create({
    outer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
    },
});
